import {
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaCheck, FaPlus } from "react-icons/fa";
import {
  TextDocumentDto,
  useCreateUploadForTextDocumentMutation,
  useUpdateTextDocumentMutation,
} from "../../autogen/bff-api";
import type { PanelPlacement } from "../agent/panelPlacement";
import { UnsavedChangesError } from "../editing/save-coordinator/UnsavedChangesError";
import { useApiError } from "../errors/useApiError";
import { flushPendingSaves, PendingSavesProvider, usePendingSavesRegistry } from "../input/pendingSaves";
import { usePanelLayout } from "../layout/panelLayoutContext";
import { guardLeavingPage } from "../navigation/leaveGuard";
import { useConfirmationModal } from "../useConfirmationModal";
import { getFileExtension } from "./utils";
import { TipTap } from "../input/TipTap/TipTap";
import { AutofillModal } from "./AutofillModal";

type DocumentChange = { title?: string; content?: string; tags?: string[] };

const roomLeftBy = (placement: PanelPlacement) =>
  placement.kind === "fullscreen" ? { display: "none" } : { width: `calc(100vw - ${placement.widthPx}px)` };

const changedFields = (change: DocumentChange) =>
  (Object.keys(change) as (keyof DocumentChange)[]).filter((field) => change[field] !== undefined);

export const TextDocumentModal = ({
  textDocument,
  contractId,
  onClose,
  onUpdate,
  isUpdating,
  editable = true,
}: {
  textDocument: TextDocumentDto;
  contractId?: string;
  onClose: () => void;
  onUpdate?: ({ title, content, tags }: { title?: string; content?: string; tags?: string[] }) => unknown;
  isUpdating: boolean;
  editable?: boolean;
}) => {
  const [name, setName] = useState<string>();
  const [modifiedAt, setModifiedAt] = useState<Date>();
  const [tags, setTags] = useState<string[]>();
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [showAutofillModal, setShowAutofillModal] = useState(false);
  const [editorContent, setEditorContent] = useState(textDocument.text);
  const [createUploadForTextDocument] = useCreateUploadForTextDocumentMutation();
  const [removeEmbeddedDocument] = useUpdateTextDocumentMutation();
  const { trigger } = useApiError();
  const createTextDocUpload = useCallback(
    async ({ uploadId, file }: { uploadId: string; file: File }) =>
      createUploadForTextDocument({
        textDocumentId: textDocument.id,
        uploadId,
        createUploadRequestDto: {
          fileName: file.name,
          fileSize: file.size,
          fileExtension: getFileExtension(file.name),
          mimeType: file.type,
        },
      }).unwrap(),
    [createUploadForTextDocument, textDocument.id],
  );

  const failedSaves = useRef(new Map<keyof DocumentChange, { change: DocumentChange; error: unknown }>());
  const latestSaves = useRef(new Map<keyof DocumentChange, Promise<unknown>>());
  const closing = useRef(false);
  const discarded = useRef(false);

  const save = async (change: DocumentChange) => {
    if (discarded.current) throw new UnsavedChangesError("saveFailed", true);
    const fields = changedFields(change);
    const sent = Promise.resolve().then(() => onUpdate?.(change));
    fields.forEach((field) => latestSaves.current.set(field, sent));
    const isLatest = (field: keyof DocumentChange) => latestSaves.current.get(field) === sent;
    try {
      await sent;
      setModifiedAt(new Date());
      fields.filter(isLatest).forEach((field) => failedSaves.current.delete(field));
    } catch (error) {
      fields
        .filter(isLatest)
        .forEach((field) => failedSaves.current.set(field, { change: { [field]: change[field] }, error }));
      if (!closing.current) {
        trigger(error, t("The document could not be saved. Your changes are still here, so try again.") ?? undefined);
      }
      throw error;
    }
  };
  const saveDetails = (change: DocumentChange) => save(change).catch(() => undefined);

  const discardChoice = useConfirmationModal();
  const offerToDiscard = useRef(() => undefined as void);
  offerToDiscard.current = () =>
    discardChoice.show({
      title: t("The changes were not saved"),
      description: t(
        "The document could not be saved. Do you want to close without saving? The changes that are not saved will be lost.",
      ),
      cancelButtonText: t("Keep editing") ?? undefined,
      confirmButtonText: t("Close without saving") ?? undefined,
      confirmButtonColor: "red",
      size: "md",
      onConfirmed: () => {
        discarded.current = true;
        onClose();
      },
    });

  const { placement } = usePanelLayout();
  const room = placement ? roomLeftBy(placement) : undefined;

  const editorSaves = usePendingSavesRegistry();
  const [isOpeningAutofill, setIsOpeningAutofill] = useState(false);
  const openAutofillOnceSaved = async () => {
    setIsOpeningAutofill(true);
    try {
      await flushPendingSaves(editorSaves);
    } catch {
      return;
    } finally {
      setIsOpeningAutofill(false);
    }
    setShowAutofillModal(true);
  };
  const [isClosing, setIsClosing] = useState(false);
  const close = async () => {
    if (closing.current) return;
    closing.current = true;
    setIsClosing(true);
    let saved = true;
    try {
      await flushPendingSaves(editorSaves);
      await Promise.allSettled(latestSaves.current.values());
      for (const { change } of [...failedSaves.current.values()]) await save(change);
    } catch {
      saved = false;
    } finally {
      closing.current = false;
      setIsClosing(false);
    }
    if (saved) onClose();
    else offerToDiscard.current();
  };

  useEffect(
    () =>
      guardLeavingPage(() => {
        if (discarded.current || failedSaves.current.size === 0) return true;
        offerToDiscard.current();
        return false;
      }),
    [],
  );

  useEffect(() => {
    setName(textDocument.name);
  }, [textDocument.name]);

  useEffect(() => {
    setTags(textDocument.tags);
  }, [textDocument.tags]);

  useEffect(() => {
    setEditorContent(textDocument.text);
  }, [textDocument.text]);

  if (showAutofillModal && contractId) {
    return (
      <AutofillModal
        contractId={contractId}
        documentContent={editorContent}
        onClose={() => setShowAutofillModal(false)}
        onApplyContent={async (content) => {
          setEditorContent(content);
          await saveDetails({ content });
        }}
      />
    );
  }

  return (
    <>
      <Modal
        isOpen={!!textDocument}
        onClose={close}
        size="3xl"
        trapFocus={!placement}
        useInert={!placement}
        blockScrollOnMount={!placement}
      >
        <ModalOverlay {...room} />
        <ModalContent containerProps={room} height="80%" maxHeight="80%" overflow="hidden">
          <ModalCloseButton right="4" />
          {(isUpdating || isClosing) && <Spinner position="absolute" top="2" left="6" color="smMuted" />}
          <ModalBody width="full" height="full" maxHeight="full">
            <Flex
              justifyContent="start"
              flexDirection={"column"}
              height={"full"}
              maxHeight="full"
              overflowY="hidden"
              width="full"
              pt="4"
              pb="6"
            >
              <Flex flexDirection="column" columnGap="2" pt="5" pb="3" px="1px">
                <Input
                  placeholder={`${t("Document name")}...`}
                  value={name ?? ""}
                  isDisabled={!editable}
                  _disabled={{ opacity: 1 }}
                  fontWeight="semibold"
                  onChange={async (e) => {
                    setName(e.target.value);
                    await saveDetails({ title: e.target.value });
                  }}
                />
              </Flex>
              <Flex flexWrap="wrap" columnGap="1" rowGap="1">
                {tags?.map((tag) => (
                  <Tag key={tag} size="sm" alignItems="center">
                    <TagLabel>{tag}</TagLabel>
                    {editable && (
                      <TagCloseButton
                        aria-label={t("Remove tag") ?? undefined}
                        onClick={async () => {
                          const updatedTags = tags.filter((t) => t !== tag);
                          setTags(updatedTags);
                          await saveDetails({ tags: updatedTags });
                        }}
                      />
                    )}
                  </Tag>
                ))}
                {editable && !showTagInput && (
                  <Button
                    size="xs"
                    variant="outline"
                    rightIcon={<Icon as={FaPlus} />}
                    colorScheme="teal"
                    onClick={() => setShowTagInput(true)}
                  >
                    {t("Add tag")}
                  </Button>
                )}
                {showTagInput && (
                  <InputGroup display="inline-flex" width="40">
                    <Input size="xs" rounded="md" pr="6" value={newTag} onChange={(e) => setNewTag(e.target.value)} />
                    <InputRightElement width="6" height="6">
                      <IconButton
                        size="xs"
                        aria-label={t("Save tag")}
                        icon={<Icon as={FaCheck} />}
                        roundedLeft="none"
                        colorScheme="teal"
                        onClick={async () => {
                          const updatedTags = Array.from(new Set([...(tags ?? []), newTag]));
                          setTags(updatedTags);
                          await saveDetails({ tags: updatedTags });
                          setNewTag("");
                          setShowTagInput(false);
                        }}
                      />
                    </InputRightElement>
                  </InputGroup>
                )}
              </Flex>
              <Text fontSize="sm" color="smMuted" pt="2" pb="1">{`${t("Last updated")} ${(
                modifiedAt ?? new Date(textDocument.modifiedAt)
              ).toLocaleString()}`}</Text>
              <Flex flexDirection="column" grow="1" overflow="hidden">
                <PendingSavesProvider registry={editorSaves}>
                  <TipTap
                    ariaLabel={name || (t("Document text") ?? "")}
                    editable={editable}
                    content={editorContent}
                    maxHeight="full"
                    createPostRequest={createTextDocUpload}
                    onAutofill={contractId && editable ? () => void openAutofillOnceSaved() : undefined}
                    isOpeningAutofill={isOpeningAutofill}
                    askFurtherAbout={name ? { source: name } : undefined}
                    toolbar={contractId && editable ? { autofill: true } : { autofill: false }}
                    onUpdate={async (content) => {
                      setEditorContent(content);
                      await save({ content });
                    }}
                    onDocumentRemove={async ({ documentId }) => {
                      await removeEmbeddedDocument({
                        updateTextDocumentRequest: { id: textDocument.id, removeEmbeddedDocument: documentId },
                      }).unwrap();
                    }}
                    documents={textDocument.documents}
                    textDocumentId={textDocument.id}
                    savesOnFlush
                  />
                </PendingSavesProvider>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      {discardChoice.modal}
    </>
  );
};
